import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DollarSign, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/api';
const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'NPR', symbol: 'रू', name: 'Nepalese Rupee' },
  { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
];

const NUMBER_FORMATS = [
  { value: '1,00,000.00', label: 'Indian (1,00,000.00)' },
  { value: '100,000.00', label: 'International (100,000.00)' },
  { value: '100.000,00', label: 'European (100.000,00)' },
  { value: '100 000.00', label: 'Space Separator (100 000.00)' },
];

const CurrencySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    currency_code: 'INR',
    currency_symbol: '₹',
    currency_name: 'Indian Rupee',
    symbol_position: 'before',
    decimal_separator: '.',
    thousand_separator: ',',
    decimal_places: 2,
    number_format: '1,00,000.00'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/master-settings/currency-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        // Cache in localStorage
        localStorage.setItem('currencySettings', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error fetching currency settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = (code) => {
    const currency = CURRENCIES.find(c => c.code === code);
    if (currency) {
      setSettings(prev => ({
        ...prev,
        currency_code: currency.code,
        currency_symbol: currency.symbol,
        currency_name: currency.name
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/master-settings/currency-settings', settings);
      localStorage.setItem('currencySettings', JSON.stringify(settings));
      toast.success('Currency settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatPreview = (amount) => {
    const { symbol_position, currency_symbol, decimal_places, thousand_separator, decimal_separator, number_format } = settings;
    
    let formatted = amount.toFixed(decimal_places);
    
    // Apply thousand separator based on format
    const parts = formatted.split('.');
    let intPart = parts[0];
    const decPart = parts[1] || '';
    
    if (number_format === '1,00,000.00') {
      // Indian format: 1,23,456
      intPart = intPart.replace(/\B(?=(\d{2})+(?!\d))/g, thousand_separator);
      // Fix first group
      if (intPart.length > 3) {
        const firstComma = intPart.indexOf(thousand_separator);
        if (firstComma > 0 && firstComma < 3) {
          intPart = intPart.substring(0, firstComma) + intPart.substring(firstComma);
        }
      }
      intPart = intPart.replace(/(\d)(?=(\d{2})+\d{3}$)/g, `$1${thousand_separator}`);
    } else {
      // International format
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousand_separator);
    }
    
    formatted = decPart ? `${intPart}${decimal_separator}${decPart}` : intPart;
    
    return symbol_position === 'before' 
      ? `${currency_symbol}${formatted}` 
      : `${formatted} ${currency_symbol}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="currency-settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <DollarSign className="text-green-600" size={28} />
            Currency Settings
          </h1>
          <p className="text-gray-600 mt-1">Configure currency display format for your institution</p>
        </div>
        <Button onClick={fetchSettings} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Currency Configuration</CardTitle>
            <CardDescription>Set your preferred currency and formatting options</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Currency Selection */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={settings.currency_code} onValueChange={handleCurrencyChange}>
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} - {c.name} ({c.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Symbol */}
            <div className="space-y-2">
              <Label>Currency Symbol</Label>
              <Input
                value={settings.currency_symbol}
                onChange={(e) => setSettings(prev => ({ ...prev, currency_symbol: e.target.value }))}
                placeholder="₹"
                className="w-24"
                data-testid="symbol-input"
              />
            </div>

            {/* Symbol Position */}
            <div className="space-y-2">
              <Label>Symbol Position</Label>
              <Select 
                value={settings.symbol_position} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, symbol_position: v }))}
              >
                <SelectTrigger data-testid="position-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Before Amount (₹1,000)</SelectItem>
                  <SelectItem value="after">After Amount (1,000 ₹)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Number Format */}
            <div className="space-y-2">
              <Label>Number Format</Label>
              <Select 
                value={settings.number_format} 
                onValueChange={(v) => {
                  const sep = v.includes('.00') ? '.' : ',';
                  const thousandSep = v.includes(' ') ? ' ' : (v.includes('.00') ? ',' : '.');
                  setSettings(prev => ({ 
                    ...prev, 
                    number_format: v,
                    decimal_separator: sep === '.' ? '.' : ',',
                    thousand_separator: thousandSep
                  }));
                }}
              >
                <SelectTrigger data-testid="format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NUMBER_FORMATS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Decimal Places */}
            <div className="space-y-2">
              <Label>Decimal Places</Label>
              <Select 
                value={settings.decimal_places.toString()} 
                onValueChange={(v) => setSettings(prev => ({ ...prev, decimal_places: parseInt(v) }))}
              >
                <SelectTrigger data-testid="decimal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 (No decimals)</SelectItem>
                  <SelectItem value="2">2 (Standard)</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full bg-green-600 hover:bg-green-700 mt-4"
              data-testid="save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>See how amounts will be displayed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                <span className="text-gray-600">Small Amount:</span>
                <span className="text-lg font-semibold text-gray-800">{formatPreview(1500)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                <span className="text-gray-600">Medium Amount:</span>
                <span className="text-lg font-semibold text-gray-800">{formatPreview(45000)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                <span className="text-gray-600">Large Amount:</span>
                <span className="text-lg font-semibold text-green-600 text-xl">{formatPreview(125000)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                <span className="text-gray-600">Very Large:</span>
                <span className="text-lg font-semibold text-green-600 text-xl">{formatPreview(1250000)}</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
              <h4 className="font-medium text-gray-700 mb-2">Current Settings:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-500">Currency:</span>
                <span className="font-medium">{settings.currency_name}</span>
                <span className="text-gray-500">Symbol:</span>
                <span className="font-medium">{settings.currency_symbol}</span>
                <span className="text-gray-500">Position:</span>
                <span className="font-medium capitalize">{settings.symbol_position}</span>
                <span className="text-gray-500">Format:</span>
                <span className="font-medium">{settings.number_format}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CurrencySettings;
